import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { FaUser, FaMapMarkerAlt, FaCheckCircle, FaPlus } from "react-icons/fa";
import { FiCreditCard, FiTruck, FiShield } from "react-icons/fi";

import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Loader from "../../components/Loader/Loader";
import AddressFields, { addressLine, PinStatus } from "../../components/AddressFields/AddressFields";
import { useAuth } from "../../context/AuthContext";
import { getAddresses, createAddress } from "../../api/addressApi";
import { createOrder } from "../../api/orderApi";
import { getAddOns } from "../../api/checkoutApi";
import { getLoginOffer, validateCoupon } from "../../api/couponApi";
import { getCheckoutSettings, checkPincode } from "../../api/settingsApi";
import { computeCheckout, formatDeliveryWindow } from "../../utils/checkoutTotals";
import { round2 } from "../../utils/orderPricing";
import "../Address.css";
import "./Checkout.css";

const emptyAddressForm = {
  fullName: "",
  phone: "",
  line1: "",
  line2: "",
  landmark: "",
  city: "",
  state: "",
  pincode: "",
  isDefault: false,
};

const PHONE_RE = /^\d{10}$/;
const PINCODE_RE = /^\d{6}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const money = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const offerText = (offer) =>
  offer.discountType === "percentage" ? `${Number(offer.discountValue)}% off` : `${money(offer.discountValue)} off`;

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, accessToken, initializing, phoneOtpRequired } = useAuth();

  const checkoutItems = location.state?.items;
  const summary = location.state?.summary;

  // No item to check out (direct navigation, refresh after redirect, etc.)
  useEffect(() => {
    if (!Array.isArray(checkoutItems) || checkoutItems.length === 0) {
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A guest can buy without an account - unless mobile OTP is switched on, which only works through
  // the login flow, so guests are sent there (as before).
  const guestCheckoutAllowed = !phoneOtpRequired;
  const canCheckout = isLoggedIn || guestCheckoutAllowed;

  const [settings, setSettings] = useState({
    shippingFee: 0,
    freeShippingAbove: null,
    codFee: 0,
    codEnabled: true,
    codAdvanceEnabled: false,
    codAdvanceAbove: 1500,
    codAdvanceAmount: 49,
    codAdvanceHighAbove: null,
    codAdvanceHighAmount: 99,
    codAdvanceCoversDelivery: false,
    codRequiresAccount: true,
    prepaidDiscountType: "none",
    prepaidDiscountValue: 0,
    prepaidDiscountMax: null,
    deliveryMinDays: 4,
    deliveryMaxDays: 6,
    addOnMaxPrice: 0,
  });
  useEffect(() => {
    getCheckoutSettings().then(setSettings);
  }, []);

  // PIN check: idle | checking | valid | invalid | unverified (+ serviceable and the delivery window)
  const [pinCheck, setPinCheck] = useState({ status: "idle" });
  // "" = as soon as possible, otherwise YYYY-MM-DD
  const [deliveryDate, setDeliveryDate] = useState("");

  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [showAddressForm, setShowAddressForm] = useState(!isLoggedIn);
  const [addressForm, setAddressForm] = useState(emptyAddressForm);

  // A guest's own contact details (the delivery name and phone are asked for in the address)
  const [guest, setGuest] = useState({ name: "", email: "", phone: "" });

  const [paymentMethod, setPaymentMethod] = useState("payu");
  const [placingOrder, setPlacingOrder] = useState(false);
  const [formError, setFormError] = useState("");

  // Cheap products offered at checkout, and the ones the buyer added
  const [availableAddOns, setAvailableAddOns] = useState([]);
  const [chosenAddOns, setChosenAddOns] = useState([]);

  // The coupon of this order. It starts as the one applied on the product page.
  const [coupon, setCoupon] = useState({ code: summary?.couponCode || "", discount: Number(summary?.discount || 0) });
  const [couponNote, setCouponNote] = useState("");
  const [loginOffer, setLoginOffer] = useState(null);

  useEffect(() => {
    if (!isLoggedIn) setShowAddressForm(true);
  }, [isLoggedIn]);

  // Saved addresses of a logged-in customer
  useEffect(() => {
    const fetchAddresses = async () => {
      if (!isLoggedIn) return;
      setLoadingAddresses(true);
      try {
        const result = await getAddresses(accessToken);
        const list = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.data?.addresses)
          ? result.data.addresses
          : [];
        setAddresses(list);

        if (list.length > 0) {
          const preferred = list.find((a) => a.isDefault) || list[0];
          setSelectedAddressId(preferred.uuid);
          setShowAddressForm(false);
        } else {
          setShowAddressForm(true);
        }
      } catch (err) {
        console.error("Checkout: failed to load addresses", err);
      } finally {
        setLoadingAddresses(false);
      }
    };

    fetchAddresses();
  }, [isLoggedIn, accessToken]);

  // Add-on suggestions (never the products already in the order)
  useEffect(() => {
    if (!Array.isArray(checkoutItems)) return;
    getAddOns(checkoutItems.map((i) => i.productId)).then(setAvailableAddOns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The "log in for a discount" offer, to promote creating an account (and to apply it once logged in)
  useEffect(() => {
    getLoginOffer().then((r) => setLoginOffer(r?.ok && r.data?.code ? r.data : null));
  }, []);

  const usingForm = !isLoggedIn || showAddressForm || addresses.length === 0;
  const selectedAddress = addresses.find((a) => a.uuid === selectedAddressId);

  // Everything that goes to the server for this order
  const orderItems = useMemo(
    () => [
      ...(checkoutItems || []),
      ...chosenAddOns.map((a) => ({ productId: a.id, quantity: 1, selectedChoiceIds: [] })),
    ],
    [checkoutItems, chosenAddOns]
  );
  const orderItemsKey = JSON.stringify(orderItems);

  // The coupon is worked out on ALL the goods, so adding or removing a product re-checks it (and a login-only
  // offer is applied once the customer is logged in and no coupon is on the order yet).
  const productPageItemsKey = useRef(JSON.stringify(checkoutItems || []));
  useEffect(() => {
    if (!isLoggedIn && !coupon.code) return undefined;
    let cancelled = false;

    const recheck = async (code, quiet) => {
      const result = await validateCoupon(code, orderItems, accessToken);
      if (cancelled) return;
      if (result?.success) {
        setCoupon({ code, discount: Number(result.data.discountAmount) || 0 });
        setCouponNote("");
      } else if (!quiet) {
        setCoupon({ code: "", discount: 0 });
        setCouponNote(`Coupon ${code} no longer applies to this order${result?.message ? `: ${result.message}` : "."}`);
      }
    };

    if (coupon.code) {
      // Back to exactly what was on the product page: its discount is already right. Anything else is re-checked.
      if (orderItemsKey === productPageItemsKey.current && coupon.code === summary?.couponCode) {
        setCoupon({ code: coupon.code, discount: Number(summary?.discount || 0) });
      } else {
        recheck(coupon.code, false);
      }
    } else if (isLoggedIn && loginOffer?.code) {
      recheck(loginOffer.code, true);
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItemsKey, isLoggedIn, loginOffer?.code]);

  // ---- the PIN in use: the form's, or the chosen saved address's
  const activePincode = usingForm ? addressForm.pincode.trim() : selectedAddress?.pincode || "";
  useEffect(() => {
    if (!PINCODE_RE.test(activePincode)) {
      setPinCheck({ status: "idle" });
      return undefined;
    }
    let cancelled = false;
    setPinCheck({ status: "checking" });
    checkPincode(activePincode, { cod: paymentMethod === "cod" }).then((result) => {
      if (cancelled) return;
      setPinCheck(result);
      // fill city / state from the PIN, without overwriting what was typed
      if (usingForm && result.status === "valid") {
        setAddressForm((prev) => ({
          ...prev,
          city: prev.city.trim() ? prev.city : result.city || "",
          state: prev.state.trim() ? prev.state : result.state || "",
        }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [activePincode, paymentMethod, usingForm]);

  // The delivery name and phone follow what the buyer typed about themselves, until they edit them here.
  const editedByBuyer = useRef({});
  const handleAddressChange = (name, value) => {
    if (name === "fullName" || name === "phone") editedByBuyer.current[name] = true;
    setAddressForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGuestChange = (name, value) => {
    setGuest((prev) => ({ ...prev, [name]: value }));
    if (name === "name" && !editedByBuyer.current.fullName) setAddressForm((prev) => ({ ...prev, fullName: value }));
    if (name === "phone" && !editedByBuyer.current.phone) setAddressForm((prev) => ({ ...prev, phone: value }));
  };

  // ---- COD: for customers with an account (the admin can allow guests); a guest can still pay online
  const codOffered = settings.codEnabled;
  const codNeedsLogin = !isLoggedIn && settings.codRequiresAccount;
  useEffect(() => {
    if (paymentMethod === "cod" && (!codOffered || codNeedsLogin)) setPaymentMethod("payu");
  }, [paymentMethod, codOffered, codNeedsLogin]);

  // ---- the numbers, for each way of paying, so the cards can show what each costs
  const mainSubtotal = Number(summary?.subtotal ?? Number(summary?.total || 0) + Number(summary?.discount || 0));
  const addOnTotal = round2(chosenAddOns.reduce((sum, a) => sum + Number(a.price), 0));
  const goodsSubtotal = round2(mainSubtotal + addOnTotal);
  const totalsFor = (method) =>
    computeCheckout({ settings, subtotal: goodsSubtotal, couponDiscount: coupon.discount, paymentMethod: method });
  const online = totalsFor("payu");
  const cash = totalsFor("cod");
  const current = paymentMethod === "cod" ? cash : online;

  // Delivery date choices: day after tomorrow for the next week.
  const dateChoices = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i + 2);
    const pad = (n) => String(n).padStart(2, "0");
    return {
      value: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      label: d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }),
    };
  });

  const deliveryBy = pinCheck.status === "valid" && pinCheck.serviceable !== false ? formatDeliveryWindow(pinCheck.deliveryFrom, pinCheck.deliveryTo) : "";
  const unserviceable = pinCheck.status === "valid" && pinCheck.serviceable === false;

  const validate = () => {
    if (!canCheckout) return "Please log in to continue.";

    if (!isLoggedIn) {
      if (guest.name.trim().length < 2) return "Please enter your name.";
      if (!EMAIL_RE.test(guest.email.trim())) return "Please enter a valid email address - we send your order details there.";
      if (!PHONE_RE.test(guest.phone.trim())) return "Please enter a valid 10 digit mobile number.";
    }

    if (usingForm) {
      if (!addressForm.fullName.trim()) return "Please enter the recipient's full name.";
      if (!PHONE_RE.test(addressForm.phone.trim())) return "Please enter a valid 10 digit delivery phone number.";
      if (!addressForm.line1.trim()) return "Please enter the house / flat number.";
      if (!addressForm.line2.trim()) return "Please enter the street / area.";
      if (!addressForm.city.trim()) return "Please enter the city.";
      if (!addressForm.state.trim()) return "Please enter the state.";
      if (!PINCODE_RE.test(addressForm.pincode.trim())) return "Please enter a valid 6 digit pincode.";
      if (pinCheck.status === "invalid") return "This PIN code does not look right. Please check it.";
      if (pinCheck.status === "checking") return "Checking your PIN code - one moment.";
    } else if (!selectedAddressId) {
      return "Please select a delivery address.";
    }
    if (unserviceable) return "Sorry, we cannot deliver to this PIN code yet. Please use a different delivery address.";

    if (!["payu", "cod"].includes(paymentMethod)) return "Please select a payment method.";
    if (paymentMethod === "cod" && !settings.codEnabled) return "Cash on Delivery is currently unavailable.";
    if (paymentMethod === "cod" && codNeedsLogin) return "Please log in to use Cash on Delivery, or pay online.";

    return "";
  };

  const submitPayuForm = ({ action, params }) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = action;

    Object.entries(params || {}).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = value == null ? "" : String(value);
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setFormError("");

    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setPlacingOrder(true);

    try {
      let addressId = selectedAddressId;

      // A new address is needed whenever the buyer isn't reusing a saved one - they are a guest (no saved
      // addresses), or a logged-in buyer who opened the "add new address" form.
      if (usingForm) {
        const addressPayload = {
          fullName: addressForm.fullName.trim(),
          phone: addressForm.phone.trim(),
          line1: addressForm.line1.trim(),
          line2: addressForm.line2.trim(),
          landmark: addressForm.landmark.trim(),
          city: addressForm.city.trim(),
          state: addressForm.state.trim(),
          pincode: addressForm.pincode.trim(),
          isDefault: isLoggedIn ? addressForm.isDefault : false,
        };

        const addressResult = await createAddress(addressPayload, isLoggedIn ? accessToken : undefined);

        if (!addressResult.ok || !addressResult.success) {
          throw new Error(addressResult?.message || "Could not save the delivery address.");
        }

        addressId = addressResult.data.uuid;
      }

      const orderPayload = {
        items: orderItems,
        addressId,
        paymentMethod,
        ...(deliveryDate ? { deliveryPreferredDate: deliveryDate } : {}),
        ...(coupon.code ? { couponCode: coupon.code } : {}),
        ...(!isLoggedIn ? { guestName: guest.name.trim(), guestEmail: guest.email.trim(), guestPhone: guest.phone.trim() } : {}),
      };

      const orderResult = await createOrder(orderPayload, accessToken);

      if (!orderResult.ok || !orderResult.success) {
        throw new Error(orderResult?.message || `Failed to place order (${orderResult.status})`);
      }

      const order = orderResult.data;

      // Online payment, or the advance of a COD order: both go to PayU.
      if (paymentMethod === "payu" || order?.payuPayment) {
        if (!order?.payuPayment?.action || !order?.payuPayment?.params) {
          throw new Error("Payment could not be initiated. Please try again.");
        }
        // Hands the browser off to PayU's hosted checkout page - the buyer
        // lands back on /order-confirmation once PayU posts its result.
        submitPayuForm(order.payuPayment);
        return;
      }

      navigate(`/order-confirmation?orderId=${encodeURIComponent(order.id)}&status=success&method=cod`);
    } catch (err) {
      setFormError(err.message || "Something went wrong while placing your order.");
      setPlacingOrder(false);
    }
  };

  if (!Array.isArray(checkoutItems) || checkoutItems.length === 0) {
    return null;
  }

  // While the saved login is being restored we do not yet know if this is a guest or a customer.
  if (initializing) {
    return (
      <>
        <Header />
        <div className="checkout-page">
          <Loader label="Loading checkout..." />
        </div>
        <Footer />
      </>
    );
  }

  const loginState = { redirectTo: "/checkout", carryState: { items: checkoutItems, summary } };
  const advanceIsDelivery = settings.codAdvanceCoversDelivery && cash.advance > 0 && cash.advance === cash.shippingFee;

  return (
    <>
      <Header />
      <div className="checkout-page">
        <div className="checkout-container">
          <h1>Checkout</h1>

          <form className="checkout-grid" onSubmit={handlePlaceOrder}>
            <div className="checkout-main">
              {/* STEP 1 - LOGIN (only when guests cannot check out, i.e. while mobile OTP is switched on) */}
              {!canCheckout && (
                <section className="checkout-section checkout-gate">
                  <FiShield className="checkout-gate-icon" />
                  <h2>Verify your mobile number</h2>
                  <p className="checkout-section-hint">
                    For a safe delivery we confirm every order with an OTP sent to your mobile number. Log in to
                    continue - your cart stays right here.
                  </p>
                  <Link className="checkout-place-order-btn checkout-gate-btn" to="/login" state={loginState}>
                    LOGIN WITH MOBILE OTP
                  </Link>
                </section>
              )}

              {canCheckout && (
                <>
                  {/* GUEST: contact details, and a nudge to create an account */}
                  {!isLoggedIn && (
                    <section className="checkout-section">
                      <h2>Your details</h2>
                      <p className="checkout-section-hint">
                        No account needed to pay online. We send your order details to this email and mobile number.
                      </p>
                      <div className="address-form-grid">
                        <div className="address-field">
                          <label htmlFor="guest-name">YOUR NAME</label>
                          <input
                            id="guest-name"
                            type="text"
                            autoComplete="name"
                            value={guest.name}
                            onChange={(e) => handleGuestChange("name", e.target.value)}
                            placeholder="Your full name"
                          />
                        </div>
                        <div className="address-field">
                          <label htmlFor="guest-phone">MOBILE NUMBER</label>
                          <input
                            id="guest-phone"
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel-national"
                            maxLength={10}
                            value={guest.phone}
                            onChange={(e) => handleGuestChange("phone", e.target.value)}
                            placeholder="10 digit mobile number"
                          />
                        </div>
                        <div className="address-field full-width">
                          <label htmlFor="guest-email">EMAIL</label>
                          <input
                            id="guest-email"
                            type="email"
                            autoComplete="email"
                            value={guest.email}
                            onChange={(e) => handleGuestChange("email", e.target.value)}
                            placeholder="you@example.com"
                          />
                        </div>
                      </div>
                      <div className="checkout-account-promo">
                        <p>
                          {loginOffer ? (
                            <>
                              <strong>Create an account and get {offerText(loginOffer)}</strong> on this order, track your
                              orders and pay Cash on Delivery.
                            </>
                          ) : (
                            <>
                              <strong>Have an account?</strong> Log in to track your orders and pay Cash on Delivery.
                            </>
                          )}
                        </p>
                        <Link className="checkout-link-btn" to="/login" state={loginState}>
                          Log in or create an account
                        </Link>
                      </div>
                    </section>
                  )}

                  {/* STEP 2 - ADDRESS + PIN VERIFICATION */}
                  <section className="checkout-section">
                    <div className="checkout-section-heading">
                      <h2>Delivery Address</h2>
                      {isLoggedIn && addresses.length > 0 && (
                        <button
                          type="button"
                          className="checkout-link-btn"
                          onClick={() => {
                            setShowAddressForm((prev) => !prev);
                            setFormError("");
                          }}
                        >
                          <FaPlus />
                          {showAddressForm ? "Use a saved address" : "Add new address"}
                        </button>
                      )}
                    </div>

                    {isLoggedIn && loadingAddresses ? (
                      <Loader label="Loading your addresses..." />
                    ) : !usingForm ? (
                      <div className="checkout-address-list">
                        {addresses.map((address) => (
                          <label
                            key={address.uuid}
                            className={`checkout-address-card ${selectedAddressId === address.uuid ? "active" : ""}`}
                          >
                            <input
                              type="radio"
                              name="selectedAddress"
                              checked={selectedAddressId === address.uuid}
                              onChange={() => setSelectedAddressId(address.uuid)}
                            />
                            <div className="checkout-address-body">
                              <div className="checkout-address-name">
                                <FaUser />
                                <strong>{address.fullName}</strong>
                                {address.isDefault && (
                                  <span className="default-badge">
                                    <FaCheckCircle /> DEFAULT
                                  </span>
                                )}
                              </div>
                              <p>
                                <FaMapMarkerAlt /> {addressLine(address)}
                              </p>
                              <p>
                                {address.city}, {address.state} - {address.pincode}
                              </p>
                              <p className="address-phone">Phone: {address.phone}</p>
                            </div>
                          </label>
                        ))}
                        <PinStatus pinCheck={pinCheck} />
                      </div>
                    ) : (
                      <AddressFields value={addressForm} onChange={handleAddressChange} pinCheck={pinCheck} idPrefix="checkout" />
                    )}

                    {isLoggedIn && usingForm && (
                      <div className="default-address-check">
                        <input
                          type="checkbox"
                          id="checkoutIsDefault"
                          checked={addressForm.isDefault}
                          onChange={(e) => handleAddressChange("isDefault", e.target.checked)}
                        />
                        <label htmlFor="checkoutIsDefault">Make this my default address</label>
                      </div>
                    )}
                  </section>

                  {/* STEP 3 - DELIVERY DATE */}
                  <section className="checkout-section">
                    <h2>Delivery Date</h2>
                    {deliveryBy ? (
                      <p className="checkout-delivery-by">
                        <FiTruck /> {deliveryBy}
                      </p>
                    ) : (
                      <p className="checkout-section-hint checkout-delivery-hint">
                        Enter your PIN code above to see when it will arrive.
                      </p>
                    )}
                    <p className="checkout-section-hint">
                      Choose a day you will be available, or leave it as soon as possible. You can change it later from
                      My Orders.
                    </p>
                    <div className="checkout-date-options" role="radiogroup" aria-label="Delivery date">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={deliveryDate === ""}
                        className={`checkout-date ${deliveryDate === "" ? "active" : ""}`}
                        onClick={() => setDeliveryDate("")}
                      >
                        As soon as possible
                      </button>
                      {dateChoices.map((d) => (
                        <button
                          type="button"
                          key={d.value}
                          role="radio"
                          aria-checked={deliveryDate === d.value}
                          className={`checkout-date ${deliveryDate === d.value ? "active" : ""}`}
                          onClick={() => setDeliveryDate(d.value)}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </section>

                  {/* ADD-ONS: a few cheap products, and a nudge to free delivery */}
                  {availableAddOns.filter((a) => !chosenAddOns.some((c) => c.id === a.id)).length > 0 && (
                    <section className="checkout-section checkout-addons">
                      <h2>Add to your order</h2>
                      <p className="checkout-section-hint">
                        {current.freeShippingGap > 0
                          ? `Add ${money(current.freeShippingGap)} more to get free delivery.`
                          : "Small extras that go in the same parcel."}
                      </p>
                      <div className="checkout-addon-list">
                        {availableAddOns
                          .filter((a) => !chosenAddOns.some((c) => c.id === a.id))
                          .map((addOn) => (
                            <div className="checkout-addon-card" key={addOn.id}>
                              {addOn.image && <img src={addOn.image} alt="" loading="lazy" />}
                              <strong>{addOn.name}</strong>
                              <span>{money(addOn.price)}</span>
                              <button type="button" className="checkout-addon-btn" onClick={() => setChosenAddOns((list) => [...list, addOn])}>
                                + Add
                              </button>
                            </div>
                          ))}
                      </div>
                    </section>
                  )}

                  {/* STEP 4 - PAYMENT */}
                  <section className="checkout-section">
                    <h2>Payment Method</h2>
                    {online.prepaidDiscount > 0 && (
                      <p className="checkout-prepaid-banner">
                        Pay online and save {money(online.prepaidDiscount)} on this order.
                      </p>
                    )}
                    <div className="checkout-payment-options">
                      <label className={`checkout-payment-card ${paymentMethod === "payu" ? "active" : ""}`}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="payu"
                          checked={paymentMethod === "payu"}
                          onChange={() => setPaymentMethod("payu")}
                        />
                        <FiCreditCard />
                        <div>
                          <strong>Pay Online</strong>
                          <span>UPI, Cards, Net Banking &amp; Wallets via PayU</span>
                          {online.prepaidDiscount > 0 && (
                            <span className="checkout-prepaid-badge">
                              Save {money(online.prepaidDiscount)} - you pay {money(online.total)}
                            </span>
                          )}
                        </div>
                      </label>

                      {codOffered && (
                        <label
                          className={`checkout-payment-card ${paymentMethod === "cod" ? "active" : ""} ${codNeedsLogin ? "is-disabled" : ""}`}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="cod"
                            checked={paymentMethod === "cod"}
                            disabled={codNeedsLogin}
                            onChange={() => setPaymentMethod("cod")}
                          />
                          <FiTruck />
                          <div>
                            <strong>Cash on Delivery</strong>
                            <span>
                              Pay in cash when your order arrives
                              {Number(settings.codFee) > 0 ? ` (COD fee ${money(settings.codFee)})` : ""}
                            </span>
                            {codNeedsLogin ? (
                              <span className="checkout-advance-note">
                                Cash on Delivery is for customers with an account.{" "}
                                <Link to="/login" state={loginState}>
                                  Log in
                                </Link>{" "}
                                to use it, or pay online.
                              </span>
                            ) : (
                              <>
                                {cash.advance > 0 && (
                                  <span className="checkout-advance-note">
                                    {advanceIsDelivery
                                      ? `The delivery charge (${money(cash.advance)}) is paid online to confirm your order; the rest (${money(cash.payOnDelivery)}) is paid in cash on delivery.`
                                      : `${money(cash.advance)} advance is paid online to confirm your order; the rest (${money(cash.payOnDelivery)}) is paid on delivery.`}
                                  </span>
                                )}
                                <span className="checkout-cod-confirm-note">
                                  After you order, we send a confirmation link by WhatsApp / email. Tap Confirm so we can dispatch it.
                                </span>
                              </>
                            )}
                          </div>
                        </label>
                      )}
                    </div>
                  </section>
                </>
              )}

              {formError && (
                <div className="address-error" role="alert">
                  {formError}
                </div>
              )}
            </div>

            {/* SUMMARY */}
            <aside className="checkout-summary">
              <h2>Order Summary</h2>

              {summary?.title && (
                <div className="checkout-summary-item">
                  {summary.image && <img src={summary.image} alt={summary.title} />}
                  <div>
                    <strong>{summary.title}</strong>
                    {summary.optionsLabel && <p>{summary.optionsLabel}</p>}
                    <span>
                      Qty: {summary.quantity || 1}
                      {summary.unitPrice > 0 && (summary.quantity || 1) > 1 && ` × ${money(summary.unitPrice)}`}
                    </span>
                  </div>
                </div>
              )}

              {chosenAddOns.map((addOn) => (
                <div className="checkout-summary-item checkout-summary-addon" key={addOn.id}>
                  {addOn.image && <img src={addOn.image} alt="" />}
                  <div>
                    <strong>{addOn.name}</strong>
                    <span>
                      Qty: 1 × {money(addOn.price)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="checkout-addon-remove"
                    aria-label={`Remove ${addOn.name}`}
                    onClick={() => setChosenAddOns((list) => list.filter((a) => a.id !== addOn.id))}
                  >
                    &times;
                  </button>
                </div>
              ))}

              <div className="checkout-summary-row">
                <span>Product Price</span>
                <strong>{money(current.subtotal)}</strong>
              </div>

              <div className="checkout-summary-row">
                <span>Shipping</span>
                <strong>{current.shippingFee > 0 ? money(current.shippingFee) : "FREE"}</strong>
              </div>

              {paymentMethod === "cod" && (
                <div className="checkout-summary-row">
                  <span>COD Fee</span>
                  <strong>{current.codFee > 0 ? money(current.codFee) : "₹0"}</strong>
                </div>
              )}

              {current.discount > 0 && (
                <div className="checkout-summary-row">
                  <span>Discount{coupon.code ? ` (${coupon.code})` : ""}</span>
                  <strong className="checkout-discount">− {money(current.discount)}</strong>
                </div>
              )}

              {current.prepaidDiscount > 0 && (
                <div className="checkout-summary-row">
                  <span>Prepaid discount</span>
                  <strong className="checkout-discount">− {money(current.prepaidDiscount)}</strong>
                </div>
              )}

              {couponNote && <p className="checkout-coupon-note">{couponNote}</p>}

              {current.freeShippingGap > 0 && (
                <p className="checkout-free-ship">Add {money(current.freeShippingGap)} more for free shipping</p>
              )}

              <div className="checkout-summary-row checkout-summary-total">
                <span>Total Payable</span>
                <strong>{money(current.total)}</strong>
              </div>

              {current.advance > 0 && (
                <>
                  <div className="checkout-summary-row checkout-advance-row">
                    <span>Pay now (advance)</span>
                    <strong>{money(current.advance)}</strong>
                  </div>
                  <div className="checkout-summary-row">
                    <span>Pay in cash on delivery</span>
                    <strong>{money(current.payOnDelivery)}</strong>
                  </div>
                </>
              )}

              {deliveryBy && <p className="checkout-summary-delivery">{deliveryBy}</p>}

              <button type="submit" className="checkout-place-order-btn" disabled={placingOrder || !canCheckout || unserviceable}>
                {placingOrder
                  ? "PLACING ORDER..."
                  : !canCheckout
                  ? "VERIFY MOBILE TO CONTINUE"
                  : paymentMethod === "payu"
                  ? `PAY ${money(current.total)} SECURELY`
                  : current.advance > 0
                  ? `PAY ${money(current.advance)} ADVANCE & CONFIRM`
                  : `CONFIRM ORDER (COD) ${money(current.total)}`}
              </button>
              <p className="checkout-secure-note">Secure checkout &nbsp;•&nbsp; UPI &nbsp;•&nbsp; Cards &nbsp;•&nbsp; COD</p>
            </aside>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}
